import React, { useState } from 'react';

const RED = '#bf1e2e';
const NAVY = '#014c8d';
const BG = '#080d16';
const SURFACE = '#0d1525';
const BORDER = 'rgba(255,255,255,0.08)';
const DIM = 'rgba(255,255,255,0.45)';
const TEXT = '#f0f4ff';
const TEXT2 = 'rgba(255,255,255,0.7)';

const EDGE_URL = 'https://ieszywnmoenxmrlaymtl.supabase.co/functions/v1/program-access-request';

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: SURFACE,
  border: `1px solid ${BORDER}`,
  borderRadius: 10,
  padding: '13px 16px',
  color: TEXT,
  fontSize: 15,
  fontFamily: "'Barlow', sans-serif",
  outline: 'none',
  transition: 'border-color 0.2s',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 1,
  textTransform: 'uppercase',
  color: DIM,
  marginBottom: 8,
};

export default function RequestAccessPage() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    sport: '',
    program_interest: '',
    referral_source: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [focusedField, setFocusedField] = useState('');

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.sport || !form.program_interest) {
      setError('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(EDGE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || 'Something went wrong.');
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const focused = (field: string): React.CSSProperties => ({
    ...inputStyle,
    borderColor: focusedField === field ? `rgba(191,30,46,0.5)` : BORDER,
  });

  return (
    <div style={{
      minHeight: '100vh',
      background: BG,
      fontFamily: "'Barlow', -apple-system, BlinkMacSystemFont, sans-serif",
      color: TEXT,
    }}>
      {/* Nav */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: 'rgba(8,13,22,0.95)',
        backdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${BORDER}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 40px', height: 60,
      }}>
        <a href="/" style={{ textDecoration: 'none' }}>
          <img
            src="https://www.bthd.app/bthd-email-sig-300x80-transparent.png"
            alt="BTHD"
            style={{ height: 22, width: 'auto', filter: 'brightness(0) invert(1)' }}
          />
        </a>
        <a href="/programs" style={{
          fontSize: 13, color: DIM, textDecoration: 'none', fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          ← Back to Programs
        </a>
      </div>

      <div style={{
        maxWidth: 560, margin: '0 auto',
        padding: '64px 24px 100px',
      }}>
        {success ? (
          /* ── Success screen ── */
          <div style={{
            background: 'rgba(15,168,109,0.08)',
            border: '1.5px solid rgba(15,168,109,0.3)',
            borderTop: '3px solid #0fa86d',
            borderRadius: 16, padding: '48px 40px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 48, marginBottom: 20 }}>
              <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="#0fa86d" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <div style={{
              fontFamily: "'Barlow Condensed', sans-serif",
              fontWeight: 900, fontSize: 28, color: '#0fa86d',
              textTransform: 'uppercase', marginBottom: 14, lineHeight: 1.1,
            }}>
              Request Received
            </div>
            <p style={{ fontSize: 16, color: TEXT2, lineHeight: 1.7, marginBottom: 10 }}>
              Check your email for pricing details.
            </p>
            <p style={{ fontSize: 15, color: DIM, lineHeight: 1.7, marginBottom: 36 }}>
              A BTHD representative will be in touch within 24–48 hours to confirm your access and next steps.
            </p>
            <a href="/programs" style={{
              display: 'inline-block',
              padding: '12px 28px', borderRadius: 10,
              background: SURFACE, border: `1px solid ${BORDER}`,
              color: TEXT2, fontSize: 14, fontWeight: 700,
              textDecoration: 'none',
            }}>
              ← Back to Programs
            </a>
          </div>
        ) : (
          /* ── Form ── */
          <>
            {/* Header */}
            <div style={{ marginBottom: 40 }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center',
                background: `${RED}18`, border: `1px solid ${RED}44`,
                borderRadius: 20, padding: '4px 14px', marginBottom: 20,
                fontSize: 11, fontWeight: 800, letterSpacing: 2,
                color: RED, textTransform: 'uppercase',
              }}>
                Individual Access
              </div>
              <h1 style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                fontSize: 'clamp(36px, 7vw, 52px)',
                fontWeight: 900, lineHeight: 1.0,
                color: TEXT, textTransform: 'uppercase',
                letterSpacing: -0.5, margin: '0 0 16px',
              }}>
                Request Access
              </h1>
              <p style={{ fontSize: 16, color: TEXT2, lineHeight: 1.7, margin: 0 }}>
                Takes 20 seconds. A BTHD representative will follow up within 24–48 hours with pricing and next steps.
              </p>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>

                {/* Full name */}
                <div>
                  <label style={labelStyle}>
                    Full Name <span style={{ color: RED }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={set('name')}
                    onFocus={() => setFocusedField('name')}
                    onBlur={() => setFocusedField('')}
                    placeholder="Your full name"
                    autoComplete="name"
                    style={focused('name')}
                  />
                </div>

                {/* Email */}
                <div>
                  <label style={labelStyle}>
                    Email <span style={{ color: RED }}>*</span>
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={set('email')}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField('')}
                    placeholder="you@example.com"
                    autoComplete="email"
                    style={focused('email')}
                  />
                </div>

                {/* Sport */}
                <div>
                  <label style={labelStyle}>
                    Sport <span style={{ color: RED }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={form.sport}
                    onChange={set('sport')}
                    onFocus={() => setFocusedField('sport')}
                    onBlur={() => setFocusedField('')}
                    placeholder="e.g. Basketball, Football, Soccer..."
                    style={focused('sport')}
                  />
                </div>

                {/* Program interest */}
                <div>
                  <label style={labelStyle}>
                    Which program are you interested in? <span style={{ color: RED }}>*</span>
                  </label>
                  <div style={{ position: 'relative' }}>
                    <select
                      value={form.program_interest}
                      onChange={set('program_interest')}
                      onFocus={() => setFocusedField('program')}
                      onBlur={() => setFocusedField('')}
                      style={{
                        ...focused('program'),
                        appearance: 'none',
                        cursor: 'pointer',
                        paddingRight: 44,
                        color: form.program_interest ? TEXT : DIM,
                      }}
                    >
                      <option value="" disabled>Select a program...</option>
                      <optgroup label="BTHD Early Pathways">
                        <option value="ep_stage_1">Stage 1, Foundation and Fundamentals (Ages 8 and under)</option>
                        <option value="ep_stage_2">Stage 2, Discovery (Ages 9 to 11)</option>
                        <option value="ep_stage_3">Stage 3, Profile and Branding (Ages 12 to 13)</option>
                        <option value="ep_stage_4">Stage 4, NIL Awareness and Readiness (Ages 14 to 17)</option>
                      </optgroup>
                      <optgroup label="BTHD Athlete Programs">
                        <option value="p01">Behavioural Program (P01)</option>
                        <option value="post_sport">Post-Sport Development (P02)</option>
                        <option value="career">Career Development (P03)</option>
                        <option value="p04">Financial Literacy (P04)</option>
                      </optgroup>
                    </select>
                    <svg
                      style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                      width="16" height="16" viewBox="0 0 24 24" fill="none"
                      stroke={DIM} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  </div>
                </div>

                {/* Referral source (optional) */}
                <div>
                  <label style={labelStyle}>
                    How did you hear about BTHD?{' '}
                    <span style={{ color: 'rgba(255,255,255,0.25)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>Optional</span>
                  </label>
                  <input
                    type="text"
                    value={form.referral_source}
                    onChange={set('referral_source')}
                    onFocus={() => setFocusedField('referral')}
                    onBlur={() => setFocusedField('')}
                    placeholder="Instagram, a friend, Google..."
                    style={focused('referral')}
                  />
                </div>

                {/* Error */}
                {error && (
                  <div style={{
                    padding: '12px 16px', borderRadius: 8,
                    background: `${RED}14`, border: `1px solid ${RED}44`,
                    fontSize: 13, color: '#f87171',
                  }}>
                    {error}
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    padding: '15px 32px', borderRadius: 10,
                    background: submitting ? 'rgba(191,30,46,0.5)' : RED,
                    border: 'none', color: '#fff',
                    fontFamily: "'Barlow Condensed', sans-serif",
                    fontWeight: 700, fontSize: 16,
                    letterSpacing: 1, cursor: submitting ? 'not-allowed' : 'pointer',
                    transition: 'background 0.2s',
                    width: '100%',
                  }}
                >
                  {submitting ? 'Submitting…' : 'Request Access'}
                </button>

                <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.25)', textAlign: 'center', margin: 0, lineHeight: 1.6 }}>
                  No commitment required. A BTHD representative will follow up within 24–48 hours.
                </p>
              </div>
            </form>

            {/* Program cards reference */}
            <div style={{
              marginTop: 48, padding: '24px 28px',
              background: SURFACE, border: `1px solid ${BORDER}`,
              borderRadius: 12,
            }}>
              <div style={{
                fontSize: 10, fontWeight: 800, letterSpacing: 2,
                color: DIM, textTransform: 'uppercase', marginBottom: 16,
              }}>
                Programs Available
              </div>
              {[
                { code: 'EP', name: 'Early Pathways', color: RED, desc: '4 stages · 9 prompts per stage · Foundation, Discovery, Profile and Branding, NIL Readiness' },
                { code: 'P01', name: 'Behavioural Program', color: RED, desc: '15 weeks · 4 stages · Athlete Assessment, Skills and Pathways, Leadership, Lasting Impact' },
                { code: 'P02', name: 'Post-Sport Development', color: NAVY, desc: '15 weeks · 5 phases · Identity, purpose, community, and legacy' },
                { code: 'P03', name: 'Career Development', color: 'rgba(255,255,255,0.6)', desc: '15 weeks · 5 phases · Skills audit, industry mapping, resume, networking, offers' },
                { code: 'P04', name: 'Financial Literacy', color: 'rgba(255,255,255,0.6)', desc: '15 weeks · 5 phases · Money beliefs, financial reality, structure, protection, and wealth' },
              ].map(p => (
                <div key={p.code} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 14,
                  marginBottom: 14,
                }}>
                  <div style={{
                    flexShrink: 0, padding: '3px 8px', borderRadius: 4,
                    background: `rgba(255,255,255,0.06)`,
                    border: `1px solid rgba(255,255,255,0.1)`,
                    fontFamily: "'Barlow Condensed', sans-serif",
                    fontSize: 10, fontWeight: 900, letterSpacing: 1,
                    color: p.color, marginTop: 2,
                  }}>{p.code}</div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 2 }}>{p.name}</div>
                    <div style={{ fontSize: 12, color: DIM }}>{p.desc}</div>
                  </div>
                </div>
              ))}
              <a href="/programs" style={{
                display: 'inline-block', marginTop: 4,
                fontSize: 13, color: RED, fontWeight: 700, textDecoration: 'none',
              }}>
                View full program details →
              </a>
            </div>
          </>
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;800;900&family=Barlow:wght@400;600;700;800&display=swap');
        * { box-sizing: border-box; }
        input::placeholder, textarea::placeholder { color: rgba(255,255,255,0.22); }
        select option { background: #0d1525; color: #f0f4ff; }
        select optgroup { background: #080d16; color: rgba(255,255,255,0.4); font-size: 11px; font-weight: 700; letter-spacing: 1px; }
        @media (max-width: 600px) {
          nav { padding: 0 16px !important; }
          div[style*="padding: '64px 24px"] { padding: 40px 16px 80px !important; }
        }
      `}</style>
    </div>
  );
}
